/// Pinia store — device model config (SYS-tag-model).
///
/// A STORE, not a composable, on purpose: the model list is not page state. The
/// capability flags gate UI on asset detail, card detail and both dashboards, so the
/// registry in `shared/config/tag-spec` is hydrated once per session and read
/// synchronously everywhere. The admin screen (システム設定 > BLEトラッカーモデル) reuses the same
/// store for its CRUD so a save refreshes what every other screen already reads.
///
/// `error` = list load error (replaces the table); `actionFieldErrors` = per-field
/// mutation errors (shown under the input). General errors are toasted by handleApiError.
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { apiErrorMessage as formatError } from '@/core/api/api-error'
import { handleApiError } from '@/core/api/handle-api-error'
import { tagModelApi } from '@/features/settings/api/tag-model-api'
import { useSystemConfigStore } from '@/features/settings/stores/system-config-store'
import { TAG_SPEC_SETTING_KEY } from '@/shared/config/tag-spec'
import { setTagSpecs, type TagSpec } from '@/shared/config/tag-spec'
import type {
  CreateTagModelRequest,
  TagModelSpecResponse,
  UpdateTagModelRequest,
} from '@/types/api'

/**
 * The raw setting blob → the same row shape `GET /tag-models` returns, so both paths feed
 * ONE `applyToRegistry`. Missing fields fall back safely; the server is the strict validator.
 */
function fromSettingBlob(blob: Record<string, unknown>): TagModelSpecResponse[] {
  return Object.entries(blob).map(([model, v]) => {
    const e = (v ?? {}) as Record<string, unknown>
    return {
      model,
      getLocation: e.getLocation === true,
      buzzer: e.buzzer === true,
      getBatteryPercent: e.getBatteryPercent === true,
      chartColor: typeof e.chartColor === 'string' ? e.chartColor : '#9ca3af',
      updatedAt: typeof e.updatedAt === 'string' ? e.updatedAt : null,
      createdAt: typeof e.createdAt === 'string' ? e.createdAt : null,
      // Số đếm này CHỈ endpoint admin biết — blob cấu hình không chứa thẻ nào. Zero ở đây
      // là "chưa biết", không phải "không có": đường này chỉ nuôi registry capability
      // (`applyToRegistry`), còn màn BLEトラッカーモデル luôn đọc từ `GET /tag-models`.
      trackerCount: 0,
    }
  })
  // KHÔNG sort ở đây: call site duy nhất là `applyToRegistry`, mà hàm đó tự sắp A→Z (bắt
  // buộc, vì đường còn lại `fetchList` nhận thứ tự "mới sửa lên đầu" từ API). Sắp hai lần
  // là hai chỗ cùng quyết định một thứ — sửa một chỗ là lệch.
}

/** API rows → the shape `shared/config/tag-spec` serves to the whole app. */
function applyToRegistry(items: TagModelSpecResponse[]): void {
  // Empty response → keep the built-in fallback rather than making every model unknown,
  // which would hide battery/buzzer app-wide. Mirrors the backend's own guard.
  if (items.length === 0) return
  const specs: Record<string, TagSpec> = Object.fromEntries(
    items.map((item) => [
      item.model,
      {
        getLocation: item.getLocation,
        buzzer: item.buzzer,
        getBatteryPercent: item.getBatteryPercent,
        chartColor: item.chartColor,
      },
    ]),
  )
  // ⚠️ SẮP LẠI A→Z Ở ĐÂY, đừng tin thứ tự của `items`.
  //
  // Hai đường nuôi registry, và chúng KHÁC thứ tự: `hydrate()` đọc client-config (đã sắp
  // theo tên), còn `fetchList()` dùng `GET /tag-models` — endpoint của màn quản lý, sắp
  // MỚI SỬA LÊN ĐẦU. Tin thứ tự caller thì chỉ cần Super Admin mở màn BLEトラッカーモデル là
  // registry của session đó đảo sang thứ tự theo mốc sửa, kéo theo card 「BLEトラッカー」 trên
  // dashboard Corp Admin — nó duyệt `tagModelDisplay()` để dựng các dòng và chỉ hiện 4 dòng,
  // nên thứ tự quyết định luôn DÒNG NÀO được hiện. Đúng thứ mà `sortTagModelKeys` bên BE cố ý
  // giữ A→Z. Bug này đã có thật một lần (2026-09-09) vì `fetchList` truyền thẳng thứ tự API
  // vào đây. (Biểu đồ tròn Super Admin KHÔNG bị: nó chỉ lấy màu từ registry.)
  setTagSpecs(
    specs,
    items.map((item) => item.model).sort((a, b) => a.localeCompare(b)),
  )
}

export const useTagModelStore = defineStore('tag-model', () => {
  const list = ref<TagModelSpecResponse[]>([])
  const hydrated = ref(false)
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const error = ref<string | null>(null)
  const actionFieldErrors = ref<Record<string, string[]>>({})

  function resetActionErrors(): void {
    actionFieldErrors.value = {}
  }

  /**
   * Publish the models the app already has, WITHOUT its own request.
   *
   * The setting is `target: 'all'`, so it rides along in `/system-settings/client-config`
   * — a call Web makes once per session anyway. Reading it from there costs zero extra
   * round-trips; a dedicated fetch here would just re-download the same bytes.
   * `GET /tag-models` stays for the admin screen, which additionally needs tracker counts.
   *
   * Best-effort: a missing or unparseable value leaves the built-in fallback in place, so
   * the UI degrades to the shipped models instead of losing every capability.
   */
  function hydrate(): void {
    if (hydrated.value) return
    const raw = useSystemConfigStore().value(TAG_SPEC_SETTING_KEY)
    if (!raw) return // config not loaded yet — a later call picks it up
    try {
      const parsed: unknown = JSON.parse(raw)
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        applyToRegistry(fromSettingBlob(parsed as Record<string, unknown>))
      }
    } catch {
      // keep the fallback
    }
    hydrated.value = true
  }

  /** Admin screen: (re)load the list and refresh the app-wide registry. */
  async function fetchList(): Promise<void> {
    isLoading.value = true
    error.value = null
    try {
      const items = await tagModelApi.list()
      list.value = items
      applyToRegistry(items)
      hydrated.value = true
    } catch (err) {
      error.value = formatError(err)
    } finally {
      isLoading.value = false
    }
  }

  async function create(payload: CreateTagModelRequest): Promise<boolean> {
    isSubmitting.value = true
    actionFieldErrors.value = {}
    try {
      await tagModelApi.create(payload)
      await fetchList()
      return true
    } catch (err) {
      // TAG_MODEL_DUPLICATE nay xuống dưới ô `model` (BE ném bằng `FieldConflictError`),
      // không còn toast. Lỗi không gắn được field (mạng, 500…) vẫn do `handleApiError` toast.
      actionFieldErrors.value = handleApiError(err)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  async function update(model: string, payload: UpdateTagModelRequest): Promise<boolean> {
    isSubmitting.value = true
    actionFieldErrors.value = {}
    try {
      await tagModelApi.update(model, payload)
      await fetchList()
      return true
    } catch (err) {
      // Hai mã của màn sửa đều field-scoped và đều trỏ về ô `model`: DUPLICATE /
      // NAME_LOCKED. (Ba mã về SKU đã gỡ 2026-09-14 cùng ô nhập của chúng.)
      actionFieldErrors.value = handleApiError(err)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  async function remove(model: string): Promise<boolean> {
    isSubmitting.value = true
    actionFieldErrors.value = {}
    try {
      await tagModelApi.remove(model)
      await fetchList()
      return true
    } catch (err) {
      // e.g. TAG_MODEL_IN_USE (trackers still use it) → toast; the dialog stays open.
      handleApiError(err)
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  return {
    list,
    hydrated,
    isLoading,
    isSubmitting,
    error,
    actionFieldErrors,
    resetActionErrors,
    hydrate,
    fetchList,
    create,
    update,
    remove,
  }
})
